import { ChevronDownIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import React, { useContext, useState } from "react";
import { MarginLayout } from "~/components/layouts/MarginLayout";
import { ViewportContext } from "~/providers/ViewportProvider";
import { FaqContent } from "./FaqContent";

interface FaqComponentProps {
  sections: FaqContent[];
  selectedIds: number[];
}

const FaqComponent: React.FC<FaqComponentProps> = ({
  sections,
  selectedIds,
}) => {
  const { isMobile } = useContext(ViewportContext);
  const selectedIdsKey = selectedIds.join(",");
  const [openItemState, setOpenItemState] = useState<{
    selectedIdsKey: string;
    sectionIndex: number | null;
    itemIndex: number | null;
  }>({
    selectedIdsKey,
    sectionIndex: null,
    itemIndex: null,
  });
  const activeSection =
    openItemState.selectedIdsKey === selectedIdsKey
      ? openItemState.sectionIndex
      : null;
  const activeItem =
    openItemState.selectedIdsKey === selectedIdsKey
      ? openItemState.itemIndex
      : null;

  const handleSetActiveItem = (sectionIndex: number, itemIndex: number) => {
    setOpenItemState((current) => {
      const currentSection =
        current.selectedIdsKey === selectedIdsKey ? current.sectionIndex : null;
      const currentItem =
        current.selectedIdsKey === selectedIdsKey ? current.itemIndex : null;

      if (currentItem === itemIndex && currentSection === sectionIndex) {
        return {
          selectedIdsKey,
          sectionIndex: null,
          itemIndex: null,
        };
      }

      return {
        selectedIdsKey,
        sectionIndex,
        itemIndex,
      };
    });
  };

  const filteredSections = sections
    .map((section) => ({
      ...section,
      items: section.items
        .filter((item) => selectedIds.includes(item.id))
        .sort((a, b) => selectedIds.indexOf(a.id) - selectedIds.indexOf(b.id)),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <MarginLayout className="max-w-7xl flex-col gap-4 sm:py-2">
      {filteredSections.map((section, index) => (
        <div
          key={index}
          className={`faq-section my-4 mb-10 text-left ${
            isMobile ? "text-md" : "mb-20 text-lg"
          }`}
        >
          <h2
            className={`mb-4 font-semibold ${
              isMobile ? "text-lg" : "text-2xl"
            }`}
          >
            {section.title}
          </h2>
          <div className="faq-list">
            {section.items.map((item, itemIndex) => {
              const isOpen =
                activeSection === index && activeItem === itemIndex;

              return (
                <div key={itemIndex} className={`faq-item ${isOpen ? "active" : ""}`}>
                  <button
                    type="button"
                    className={clsx(
                      "faq-question group flex w-full items-center border-t border-[#C2EFDE] bg-white p-2 text-left shadow-sm transition-colors duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]",
                      isOpen
                        ? "bg-[#E8FAF4] text-[#0C1F3F]"
                        : "text-[#0C1F3F] hover:bg-[#E8FAF4] hover:text-[#0C1F3F]"
                    )}
                    onClick={() => handleSetActiveItem(index, itemIndex)}
                    aria-expanded={isOpen}
                  >
                    <span className="flex-grow">{item.question}</span>
                    <ChevronDownIcon
                      className={clsx(
                        "ml-auto h-7 w-7 flex-shrink-0 text-[#5C6370] transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:text-[#00A87A]",
                        isOpen && "rotate-180 text-[#00A87A]"
                      )}
                    />
                  </button>

                  <div
                    className={clsx(
                      "grid overflow-hidden transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]",
                      isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    )}
                    aria-hidden={!isOpen}
                  >
                    <div className="min-h-0 overflow-hidden">
                      <div
                        className={clsx(
                          "faq-answer bg-white px-4 text-sm leading-relaxed shadow-sm transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] md:text-base",
                          isOpen
                            ? "translate-y-0 pb-4 pt-4 opacity-100 text-[#5C6370]"
                            : "-translate-y-1 pb-0 pt-0 opacity-0 text-[#5C6370]"
                        )}
                      >
                        {item.answer}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </MarginLayout>
  );
};

export default FaqComponent;
